import React, { useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Plus, Edit3, Trash2, Wallet, Ban, CircleDollarSign, Users } from 'lucide-react';
import { backofficeApi, formatCurrency, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  CommissionDto,
  CommissionStatus,
  InfluencerDto,
  PassPlanDto,
  PayoutDto,
  PayoutStatus,
} from '@kardume/types';
import { COMMISSION_STATUS_LABELS, PAYOUT_STATUS_LABELS } from '@kardume/types';
import {
  PageHeader,
  Card,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  TextInput,
  CurrencyInput,
  SearchableComboBox,
  SearchInput,
  Select,
  Switch,
  StatCard,
  Tabs,
  Modal,
  ConfirmModal,
  Drawer,
  Avatar,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

/**
 * Programa de influenciadores (spec 038), para o **time do Kardume**.
 *
 * O influenciador não entra aqui: ele acompanha os próprios ganhos pela conta
 * de consumidor, em `/minha-conta/indique`. Esta tela é a contraparte - quem
 * negocia, confere e paga.
 *
 * Três abas, na ordem em que o trabalho acontece:
 *
 * 1. **Influenciadores** - o contrato e a tabela de valores. É onde o acordo
 *    vira configuração: "o Plano 02 paga R$ 25 para esta pessoa".
 * 2. **Comissões** - o que o motor apurou. Conferência, e cancelamento quando
 *    houve estorno ou fraude.
 * 3. **Pagamentos** - o fechamento por competência e o registro de que o
 *    dinheiro saiu. O pagamento em si acontece fora da plataforma.
 */

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  margin-bottom: 1rem;

  > * {
    min-width: 200px;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 0.9rem;
  }
  span {
    font-size: 0.75rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

/**
 * Chave com o rótulo ao lado, na caixa do mesmo desenho das telas de
 * funcionário. É `label`, e não `div` com onClick: clicar no texto alterna a
 * chave e o teclado alcança o controle, de graça.
 */
const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

/**
 * As duas metades da criação, lado a lado: QUEM é o influenciador e QUANTO ele
 * recebe. Empilhadas, a tabela de valores ficava abaixo da dobra do modal e
 * parecia um passo opcional - que é justamente o mal-entendido que fez o
 * contrato nascer sem valor nenhum.
 */
const PromoteGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

/** Título de coluna: mesmo peso do [SectionTitle], sem o traço que separaria. */
const ColumnTitle = styled.h4`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(p) => p.theme.textSecondary};
  margin: 0;
`;

/** Uma linha "plano → comissão" da criação. */
const PlanRateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px;
  gap: 0.6rem;
  align-items: end;
`;

/**
 * A lista rola dentro da coluna em vez de esticar o modal: com muitos planos, o
 * botão "Criar contrato" sairia da tela e a pessoa perderia a ação principal.
 */
const PlanList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-height: 320px;
  overflow-y: auto;
`;

/* --- Painel lateral: mesmo desenho do drawer de cliente (Usuários) --------- */

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.25rem;

  .name {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
  .email {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const DrawerStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const DrawerStat = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.85rem 1rem;

  .label {
    font-size: 0.7rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .value {
    margin-top: 0.25rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

/**
 * As abas ficam ABAIXO dos dados da pessoa, não sob o título.
 *
 * Nome, código e os totais valem para as duas abas - são a identidade do
 * contrato. Dentro de uma delas, trocar de aba faria a informação sumir e a
 * pessoa voltaria só para reler quem ela está editando.
 */
const DrawerTabs = styled.div`
  display: flex;
  gap: 1.25rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  margin-bottom: 1.25rem;
  overflow-x: auto;
`;

const DrawerTab = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  padding: 0.6rem 0.1rem;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.textMuted)};
  border-bottom: 2px solid ${(p) => (p.$active ? p.theme.primary : 'transparent')};
`;

const Section = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/**
 * O botão de salvar, separado do último campo por uma linha.
 *
 * Colado no campo acima ele parecia parte dele - e numa tela em que salvar
 * altera um acordo comercial, a ação precisa ter o próprio espaço.
 */
/** Par rótulo/valor só de leitura, como nos blocos do drawer de cliente. */
const Field = styled.div`
  .k {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .v {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const SectionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.35rem;
  padding-top: 0.9rem;
  border-top: 1px solid ${(p) => p.theme.border};
`;

const SectionTitle = styled.h4`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(p) => p.theme.textSecondary};
  margin: 1.25rem 0 0.4rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${(p) => p.theme.border};
`;

const Hint = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textSecondary};
`;

const RateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 160px auto;
  gap: 0.6rem;
  align-items: end;
  margin-top: 0.6rem;
`;

const COMMISSION_TONE: Record<CommissionStatus, 'secondary' | 'primary' | 'success' | 'danger'> = {
  PENDING: 'secondary',
  APPROVED: 'primary',
  PAID: 'success',
  CANCELED: 'danger',
};

const PAYOUT_TONE: Record<PayoutStatus, 'primary' | 'success' | 'danger'> = {
  OPEN: 'primary',
  PAID: 'success',
  CANCELED: 'danger',
};

/** `2026-09` -> `set/2026`. */
const competenceLabel = (competence: string): string => {
  const [year, month] = String(competence || '').split('-');
  if (!year || !month) return competence;
  const name = new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '');
  return `${name}/${year}`;
};

/** A competência do mês corrente, que é a que se fecha em 99% das vezes. */
const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

interface InfluencersPageProps {
  permissions: string[];
  isAdmin: boolean;
}

const InfluencersPage: React.FC<InfluencersPageProps> = ({ permissions, isAdmin }) => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const canManage = isAdmin || permissions.includes('ROLE_BACKOFFICE_INFLUENCER_MANAGE');

  const [tab, setTab] = useState('influencers');

  // --- Aba 1: contratos ---------------------------------------------------
  const [term, setTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const influencers = useAsync(
    () =>
      backofficeApi.listInfluencers({
        size: 50,
        term: term || undefined,
        active: activeFilter === '' ? undefined : activeFilter === 'true',
      }),
    [term, activeFilter]
  );
  const overview = useAsync(() => backofficeApi.getInfluencerOverview(), []);

  // Planos pagos, para a tabela de valores. O gratuito fica de fora porque
  // nunca gera pagamento - e sem pagamento não existe comissão (spec §RN-05).
  const plans = useAsync(() => backofficeApi.listPassPlans({ size: 100 }), []);
  const payablePlans = useMemo(
    () => (plans.data?.content ?? []).filter((plan: PassPlanDto) => plan.price > 0),
    [plans.data]
  );

  const [promoteOpen, setPromoteOpen] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [promoteRecurring, setPromoteRecurring] = useState(false);
  const [promoteNotes, setPromoteNotes] = useState('');
  /** Valores por plano digitados na criação: plano -> R$. Em branco = fora do acordo. */
  const [promoteRates, setPromoteRates] = useState<Record<string, number>>({});

  /**
   * A busca de clientes mora DENTRO do combo (`onQueryChange`), e não numa caixa
   * ao lado: são a mesma pergunta, e duas caixas faziam a pessoa digitar numa
   * para escolher na outra.
   *
   * O texto digitado passa por um atraso antes de virar requisição. Sem ele,
   * "Leonardo" dispara oito buscas que voltam fora de ordem, e a lista pisca com
   * o resultado de "Leo" depois do resultado de "Leonardo".
   */
  const [candidateTyped, setCandidateTyped] = useState('');
  const [candidateQuery, setCandidateQuery] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setCandidateQuery(candidateTyped), 300);
    return () => window.clearTimeout(id);
  }, [candidateTyped]);

  const candidates = useAsync(
    () => backofficeApi.listCustomers({ size: 20, query: candidateQuery || undefined }),
    [candidateQuery]
  );

  const [editing, setEditing] = useState<InfluencerDto | null>(null);
  const [editTab, setEditTab] = useState<'contrato' | 'valores'>('contrato');
  const [editActive, setEditActive] = useState(true);
  const [editRecurring, setEditRecurring] = useState(false);
  const [editPayoutNotes, setEditPayoutNotes] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [newRatePlanId, setNewRatePlanId] = useState('');
  const [newRateAmount, setNewRateAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState<InfluencerDto | null>(null);

  const openEdit = (influencer: InfluencerDto): void => {
    setEditing(influencer);
    setEditTab('contrato');
    setEditActive(influencer.active);
    setEditRecurring(influencer.recurringCommission);
    setEditPayoutNotes(influencer.payoutNotes ?? '');
    setEditNotes(influencer.notes ?? '');
    setNewRatePlanId('');
    setNewRateAmount(0);
  };

  const refreshEditing = async (id: string): Promise<void> => {
    setEditing(await backofficeApi.getInfluencer(id));
    influencers.reload();
    overview.reload();
  };

  const closePromote = (): void => {
    setPromoteOpen(false);
    setCandidateId('');
    setCandidateTyped('');
    setPromoteNotes('');
    setPromoteRecurring(false);
    setPromoteRates({});
  };

  /**
   * Cria o contrato E os valores por plano na mesma ação.
   *
   * Eram dois passos, e o primeiro produzia um objeto pela metade: contrato sem
   * tabela de valores não comissiona nada, em nenhum plano. Quem fechasse a tela
   * ali ficava com um influenciador que parece ativo na lista e nunca recebe -
   * e ninguém descobre isso até o fechamento do mês não ter o que fechar.
   *
   * O contrato vai primeiro porque as taxas penduram nele. Se uma taxa falhar
   * depois do contrato criado, o contrato FICA: apagá-lo desfaria uma decisão
   * comercial por causa de um erro de rede. A mensagem diz o que faltou e o
   * painel lateral abre para completar à mão.
   */
  const promote = async (): Promise<void> => {
    if (!candidateId) return;
    setSubmitting(true);
    try {
      const created = await backofficeApi.createInfluencer({
        customerId: candidateId,
        recurringCommission: promoteRecurring,
        notes: promoteNotes || undefined,
      });

      const chosen = Object.entries(promoteRates).filter(([, amount]) => amount > 0);
      const failed: string[] = [];
      for (const [planId, amount] of chosen) {
        try {
          await backofficeApi.saveInfluencerRate(created.id, { planId, amount, active: true });
        } catch {
          failed.push(payablePlans.find((plan) => plan.id === planId)?.name ?? planId);
        }
      }

      closePromote();
      influencers.reload();
      overview.reload();

      if (failed.length > 0) {
        setEditing(await backofficeApi.getInfluencer(created.id));
        showNotification(`Contrato criado, mas não salvamos o valor de: ${failed.join(', ')}.`, 'error');
      } else if (chosen.length === 0) {
        // Sem nenhum valor, o contrato não paga nada. Em vez de avisar e deixar
        // a pessoa se virar, abre o painel já no lugar de resolver.
        setEditing(await backofficeApi.getInfluencer(created.id));
        showNotification('Contrato criado. Defina quanto cada plano paga para ele comissionar.', 'success');
      } else {
        showNotification('Influenciador criado com os valores por plano.', 'success');
      }
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível criar o influenciador.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const saveContract = async (): Promise<void> => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await backofficeApi.updateInfluencer(editing.id, {
        active: editActive,
        recurringCommission: editRecurring,
        payoutNotes: editPayoutNotes || undefined,
        notes: editNotes || undefined,
      });
      await refreshEditing(editing.id);
      showNotification('Contrato atualizado.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o contrato.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const saveRate = async (planId: string, amount: number): Promise<void> => {
    if (!editing || !planId || amount <= 0) return;
    try {
      await backofficeApi.saveInfluencerRate(editing.id, { planId, amount, active: true });
      await refreshEditing(editing.id);
      setNewRatePlanId('');
      setNewRateAmount(0);
      showNotification('Valor por plano salvo.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o valor.'), 'error');
    }
  };

  const removeRate = async (rateId: string): Promise<void> => {
    if (!editing) return;
    try {
      await backofficeApi.deleteInfluencerRate(editing.id, rateId);
      await refreshEditing(editing.id);
      showNotification('Valor removido. Comissões já apuradas não mudam.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o valor.'), 'error');
    }
  };

  const endContract = async (): Promise<void> => {
    if (!removing) return;
    try {
      await backofficeApi.deleteInfluencer(removing.id);
      setRemoving(null);
      influencers.reload();
      overview.reload();
      showNotification('Contrato encerrado. O que já foi apurado continua devido.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível encerrar o contrato.'), 'error');
    }
  };

  // --- Aba 2: comissões ---------------------------------------------------
  const [commissionStatus, setCommissionStatus] = useState('');
  const [commissionCompetence, setCommissionCompetence] = useState('');
  const commissions = useAsync(
    () =>
      backofficeApi.listCommissions({
        size: 50,
        status: (commissionStatus || undefined) as CommissionStatus | undefined,
        competence: commissionCompetence || undefined,
      }),
    [commissionStatus, commissionCompetence]
  );
  const [canceling, setCanceling] = useState<CommissionDto | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const cancelCommission = async (): Promise<void> => {
    if (!canceling || !cancelReason.trim()) return;
    setSubmitting(true);
    try {
      await backofficeApi.cancelCommission(canceling.id, cancelReason.trim());
      setCanceling(null);
      setCancelReason('');
      commissions.reload();
      overview.reload();
      showNotification('Comissão cancelada.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível cancelar a comissão.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Aba 3: pagamentos --------------------------------------------------
  const payouts = useAsync(() => backofficeApi.listPayouts({ size: 50 }), []);
  const [closing, setClosing] = useState<InfluencerDto | null>(null);
  const [closeCompetence, setCloseCompetence] = useState(currentCompetence());
  const [paying, setPaying] = useState<PayoutDto | null>(null);
  const [paymentReference, setPaymentReference] = useState('');

  const closeCompetenceFor = async (): Promise<void> => {
    if (!closing) return;
    setSubmitting(true);
    try {
      await backofficeApi.createPayout(closing.id, { competence: closeCompetence });
      setClosing(null);
      payouts.reload();
      commissions.reload();
      overview.reload();
      setTab('payouts');
      showNotification('Competência fechada.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível fechar a competência.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const registerPayment = async (): Promise<void> => {
    if (!paying) return;
    setSubmitting(true);
    try {
      await backofficeApi.payPayout(paying.id, { paymentReference: paymentReference || undefined });
      setPaying(null);
      setPaymentReference('');
      payouts.reload();
      commissions.reload();
      overview.reload();
      showNotification('Pagamento registrado.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível registrar o pagamento.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPayout = async (payout: PayoutDto): Promise<void> => {
    try {
      await backofficeApi.cancelPayout(payout.id);
      payouts.reload();
      commissions.reload();
      showNotification('Fechamento cancelado. As comissões voltaram para "a conferir".', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível cancelar o fechamento.'), 'error');
    }
  };

  // ------------------------------------------------------------------------

  return (
    <>
      <PageHeader
        title="Influenciadores"
        subtitle="Contrato, valor por plano, comissões apuradas e fechamento de pagamento. O influenciador acompanha os ganhos pela conta dele."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setPromoteOpen(true)}>
              <Plus size={16} /> Novo influenciador
            </Button>
          ) : undefined
        }
      />

      {overview.data && (
        <Stats>
          <StatCard
            icon={<Users size={20} />}
            label="Contratos ativos"
            value={String(overview.data.activeInfluencers)}
          />
          <StatCard
            icon={<CircleDollarSign size={20} />}
            label="A conferir"
            value={formatCurrency(overview.data.pendingAmount)}
          />
          <StatCard
            icon={<Wallet size={20} />}
            label="A pagar"
            value={formatCurrency(overview.data.approvedAmount)}
          />
          <StatCard
            icon={<Wallet size={20} />}
            label="Pago no total"
            value={formatCurrency(overview.data.paidAmount)}
          />
        </Stats>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        ariaLabel="Seções do programa de influenciadores"
        tabs={[
          { value: 'influencers', label: 'Influenciadores' },
          { value: 'commissions', label: 'Comissões' },
          { value: 'payouts', label: 'Pagamentos' },
        ]}
      />

      {tab === 'influencers' && (
        <>
          <Filters>
            <SearchInput
              placeholder="Nome, e-mail ou CPF"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
            <Select
              label="Situação"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
              options={[
                { value: '', label: 'Todas' },
                { value: 'true', label: 'Ativos' },
                { value: 'false', label: 'Pausados' },
              ]}
            />
          </Filters>

          <DataState
            loading={influencers.loading}
            error={influencers.error}
            isEmpty={!influencers.data || influencers.data.content.length === 0}
            emptyTitle="Nenhum influenciador ainda"
            emptyDescription="Promova um cliente e defina quanto cada plano paga para ele."
            onRetry={influencers.reload}
          >
            {influencers.data && (
              <Card>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <th>Influenciador</th>
                        <th>Código</th>
                        <th>Acordo</th>
                        <th>Planos</th>
                        <th>Convertidos</th>
                        <th>A conferir</th>
                        <th>A pagar</th>
                        <th>Pago</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {influencers.data.content.map((influencer) => (
                        <tr key={influencer.id}>
                          <td>
                            <NameCell>
                              <strong>{influencer.customerName}</strong>
                              <span>{influencer.customerEmail}</span>
                            </NameCell>
                          </td>
                          <td>{influencer.affiliateCode ?? '-'}</td>
                          <td>
                            <Badge tone={influencer.active ? 'success' : 'secondary'}>
                              {influencer.active ? 'Ativo' : 'Pausado'}
                            </Badge>{' '}
                            <Badge tone={influencer.recurringCommission ? 'primary' : 'info'}>
                              {influencer.recurringCommission ? 'Recorrente' : 'Uma vez'}
                            </Badge>
                          </td>
                          <td>{influencer.rates.length}</td>
                          <td>{influencer.convertedCount}</td>
                          <td>{formatCurrency(influencer.pendingAmount)}</td>
                          <td>{formatCurrency(influencer.approvedAmount)}</td>
                          <td>{formatCurrency(influencer.paidAmount)}</td>
                          <td>
                            <RowActions>
                              <ActionButton title="Abrir contrato" onClick={() => openEdit(influencer)}>
                                <Edit3 size={16} />
                              </ActionButton>
                              {canManage && (
                                <>
                                  <ActionButton
                                    title="Fechar competência"
                                    onClick={() => {
                                      setClosing(influencer);
                                      setCloseCompetence(currentCompetence());
                                    }}
                                  >
                                    <Wallet size={16} />
                                  </ActionButton>
                                  {/* Vermelho e com tooltip, como o excluir das
                                      outras listas do backoffice: ação destrutiva
                                      não pode parecer igual às vizinhas. */}
                                  <ActionButton
                                    title="Excluir"
                                    color={theme.error}
                                    onClick={() => setRemoving(influencer)}
                                  >
                                    <Trash2 size={16} />
                                  </ActionButton>
                                </>
                              )}
                            </RowActions>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </DataState>
        </>
      )}

      {tab === 'commissions' && (
        <>
          <Filters>
            <Select
              label="Situação"
              value={commissionStatus}
              onChange={(event) => setCommissionStatus(event.target.value)}
              options={[
                { value: '', label: 'Todas' },
                ...Object.entries(COMMISSION_STATUS_LABELS).map(([value, label]) => ({ value, label })),
              ]}
            />
            <TextInput
              label="Competência"
              placeholder="AAAA-MM"
              value={commissionCompetence}
              onChange={(event) => setCommissionCompetence(event.target.value)}
            />
          </Filters>

          <DataState
            loading={commissions.loading}
            error={commissions.error}
            isEmpty={!commissions.data || commissions.data.content.length === 0}
            emptyTitle="Nenhuma comissão apurada"
            emptyDescription="A comissão nasce quando um indicado paga um plano com valor combinado."
            onRetry={commissions.reload}
          >
            {commissions.data && (
              <Card>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <th>Influenciador</th>
                        <th>Indicado</th>
                        <th>Plano</th>
                        <th>Cobrança</th>
                        <th>Comissão</th>
                        <th>Competência</th>
                        <th>Situação</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.data.content.map((commission) => (
                        <tr key={commission.id}>
                          <td>{commission.influencerName}</td>
                          <td>{commission.referredCustomerName ?? '-'}</td>
                          <td>{commission.planName}</td>
                          <td>{formatCurrency(commission.baseAmount)}</td>
                          <td>
                            <strong>{formatCurrency(commission.amount)}</strong>
                          </td>
                          <td>{competenceLabel(commission.competence)}</td>
                          <td>
                            <Badge tone={COMMISSION_TONE[commission.status]}>
                              {COMMISSION_STATUS_LABELS[commission.status]}
                            </Badge>
                            {commission.canceledReason && <Hint>{commission.canceledReason}</Hint>}
                          </td>
                          <td>
                            <RowActions>
                              {canManage && commission.status !== 'PAID' && commission.status !== 'CANCELED' && (
                                <ActionButton
                                  title="Cancelar comissão"
                                  color={theme.error}
                                  onClick={() => {
                                    setCanceling(commission);
                                    setCancelReason('');
                                  }}
                                >
                                  <Ban size={16} />
                                </ActionButton>
                              )}
                            </RowActions>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </DataState>
        </>
      )}

      {tab === 'payouts' && (
        <DataState
          loading={payouts.loading}
          error={payouts.error}
          isEmpty={!payouts.data || payouts.data.content.length === 0}
          emptyTitle="Nenhum fechamento"
          emptyDescription="Feche uma competência na aba Influenciadores para gerar o lote de pagamento."
          onRetry={payouts.reload}
        >
          {payouts.data && (
            <Card>
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Influenciador</th>
                      <th>Competência</th>
                      <th>Comissões</th>
                      <th>Total</th>
                      <th>Situação</th>
                      <th>Comprovante</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.data.content.map((payout) => (
                      <tr key={payout.id}>
                        <td>{payout.influencerName}</td>
                        <td>{competenceLabel(payout.competence)}</td>
                        <td>{payout.commissionCount}</td>
                        <td>
                          <strong>{formatCurrency(payout.totalAmount)}</strong>
                        </td>
                        <td>
                          <Badge tone={PAYOUT_TONE[payout.status]}>{PAYOUT_STATUS_LABELS[payout.status]}</Badge>
                        </td>
                        <td>{payout.paymentReference ?? '-'}</td>
                        <td>
                          <RowActions>
                            {canManage && payout.status === 'OPEN' && (
                              <>
                                <ActionButton
                                  title="Registrar pagamento"
                                  onClick={() => {
                                    setPaying(payout);
                                    setPaymentReference('');
                                  }}
                                >
                                  <Wallet size={16} />
                                </ActionButton>
                                <ActionButton
                                  title="Cancelar fechamento"
                                  color={theme.error}
                                  onClick={() => cancelPayout(payout)}
                                >
                                  <Ban size={16} />
                                </ActionButton>
                              </>
                            )}
                          </RowActions>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </DataState>
      )}

      {/* --- Promover cliente ------------------------------------------- */}
      <Modal
        isOpen={promoteOpen}
        onClose={closePromote}
        title="Novo influenciador"
        width="860px"
        footer={
          <>
            <Button variant="ghost" onClick={closePromote} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={promote} isLoading={submitting} disabled={!candidateId}>
              Criar contrato
            </Button>
          </>
        }
      >
        <PromoteGrid>
          <Column>
            <ColumnTitle>Quem é</ColumnTitle>
            {/* Uma caixa só: o combo busca no servidor enquanto a pessoa digita
                (`onQueryChange`). Antes havia um campo de busca ao lado, e as
                duas caixas faziam a mesma pergunta - digitava numa para
                escolher na outra. */}
            <SearchableComboBox
              label="Cliente"
              placeholder="Busque por nome, e-mail ou CPF"
              valueId={candidateId}
              onChange={setCandidateId}
              onQueryChange={setCandidateTyped}
              loading={candidates.loading}
              noResultsText="Nenhum cliente encontrado"
              required
              items={(candidates.data?.content ?? []).map((customer) => ({
                id: customer.id,
                label: `${customer.firstName} ${customer.lastName}${customer.email ? ` · ${customer.email}` : ''}`,
              }))}
            />
            <Hint>
              O influenciador usa o mesmo código de afiliado que ele já tem como cliente - não
              existe código separado.
            </Hint>

            <ToggleField>
              <Switch checked={promoteRecurring} onChange={setPromoteRecurring} />
              Comissão a cada renovação
            </ToggleField>
            <Hint>
              Desligado, paga uma vez por pessoa indicada. Ligado, paga a cada cobrança paga dela.
              É o que se negocia caso a caso.
            </Hint>

            <TextInput
              label="Observações do acordo"
              value={promoteNotes}
              onChange={(event) => setPromoteNotes(event.target.value)}
            />
          </Column>

          <Column>
            <ColumnTitle>Quanto cada plano paga</ColumnTitle>
            <Hint>
              Preencha só os planos que fazem parte do acordo. Plano em branco não gera comissão
              nenhuma - e os valores são desta pessoa, não do plano: outro influenciador pode ter
              valores diferentes nos mesmos planos.
            </Hint>

            {payablePlans.length === 0 ? (
              <Hint>
                Nenhum plano pago cadastrado no Kardume Pass. Sem plano pago não existe pagamento,
                e sem pagamento não existe comissão.
              </Hint>
            ) : (
              <PlanList>
                {payablePlans.map((plan) => (
                  <PlanRateRow key={plan.id}>
                    <TextInput
                      label="Plano"
                      value={`${plan.name} · ${formatCurrency(plan.price)}`}
                      disabled
                    />
                    <CurrencyInput
                      label="Comissão"
                      allowEmpty
                      value={promoteRates[plan.id]}
                      onChange={(value) =>
                        setPromoteRates((prev) => {
                          const next = { ...prev };
                          if (value && value > 0) next[plan.id] = value;
                          else delete next[plan.id];
                          return next;
                        })
                      }
                    />
                  </PlanRateRow>
                ))}
              </PlanList>
            )}
          </Column>
        </PromoteGrid>
      </Modal>

      {/* --- Contrato + tabela de valores -------------------------------- */}
      <Drawer
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Influenciador - ${editing.customerName}` : 'Influenciador'}
        width="620px"
        headerActions={
          editing && (
            <>
              <Badge tone={editing.active ? 'success' : 'secondary'}>
                {editing.active ? 'Ativo' : 'Pausado'}
              </Badge>
              <Badge tone={editing.recurringCommission ? 'primary' : 'info'}>
                {editing.recurringCommission ? 'Recorrente' : 'Uma vez'}
              </Badge>
            </>
          )
        }
      >
        {editing && (
          <>
            {/* Identidade do contrato: vale para as duas abas, então fica ACIMA
                delas. Dentro de uma aba, trocar de aba faria sumir justamente o
                "de quem é isto" que a pessoa precisa para conferir o valor. */}
            <Head>
              <Avatar name={editing.customerName} />
              <div>
                <div className="name">{editing.customerName}</div>
                <div className="email">{editing.customerEmail ?? '-'}</div>
              </div>
            </Head>

            <DrawerStats>
              <DrawerStat>
                <div className="label">A conferir</div>
                <div className="value">{formatCurrency(editing.pendingAmount)}</div>
              </DrawerStat>
              <DrawerStat>
                <div className="label">A pagar</div>
                <div className="value">{formatCurrency(editing.approvedAmount)}</div>
              </DrawerStat>
              <DrawerStat>
                <div className="label">Já pago</div>
                <div className="value">{formatCurrency(editing.paidAmount)}</div>
              </DrawerStat>
              <DrawerStat>
                <div className="label">Indicados convertidos</div>
                <div className="value">{editing.convertedCount}</div>
              </DrawerStat>
            </DrawerStats>

            <Section style={{ marginBottom: '1.25rem' }}>
              <Field>
                <div className="k">Código de convite</div>
                <div className="v">{editing.affiliateCode ?? '-'}</div>
              </Field>
              {editing.inviteLink && (
                <Field>
                  <div className="k">Link</div>
                  <div className="v">{editing.inviteLink}</div>
                </Field>
              )}
            </Section>

            <DrawerTabs>
              <DrawerTab
                type="button"
                $active={editTab === 'contrato'}
                onClick={() => setEditTab('contrato')}
              >
                Contrato
              </DrawerTab>
              <DrawerTab
                type="button"
                $active={editTab === 'valores'}
                onClick={() => setEditTab('valores')}
              >
                Valores por plano
              </DrawerTab>
            </DrawerTabs>

            {editTab === 'contrato' && (
              <Section>
                {/* Mesmo desenho do modal de criação e das telas de funcionário:
                    as duas chaves da mesma funcionalidade não podem ter
                    aparências diferentes dependendo de onde se abre. */}
                <ToggleField>
                  <Switch checked={editActive} onChange={setEditActive} disabled={!canManage} />
                  Contrato ativo
                </ToggleField>
                <Hint>Pausar suspende comissão nova. O que já foi apurado continua devido.</Hint>

                <ToggleField>
                  <Switch checked={editRecurring} onChange={setEditRecurring} disabled={!canManage} />
                  Comissão a cada renovação
                </ToggleField>
                <Hint>Desligado, paga uma vez por pessoa indicada.</Hint>

                <TextInput
                  label="Como pagamos (Pix, conta, combinado)"
                  value={editPayoutNotes}
                  onChange={(event) => setEditPayoutNotes(event.target.value)}
                  disabled={!canManage}
                />
                <TextInput
                  label="Observações do acordo"
                  value={editNotes}
                  onChange={(event) => setEditNotes(event.target.value)}
                  disabled={!canManage}
                />

                {canManage && (
                  <SectionActions>
                    <Button variant="primary" onClick={saveContract} isLoading={submitting}>
                      Salvar contrato
                    </Button>
                  </SectionActions>
                )}
              </Section>
            )}

            {editTab === 'valores' && (
              <Section>
                <Hint>
                  Plano que não estiver nesta lista não gera comissão nenhuma. Planos gratuitos não
                  aparecem: sem pagamento não há comissão. Alterar um valor não muda comissão já
                  apurada.
                </Hint>

                {editing.rates.length === 0 && (
                  <Hint>Nenhum valor combinado ainda - este contrato não comissiona nada.</Hint>
                )}

                {editing.rates.map((rate) => (
                  <RateRow key={rate.id}>
                    <TextInput label="Plano" value={rate.planName} disabled />
                    <CurrencyInput
                      label="Comissão"
                      value={rate.amount}
                      onChange={(value) => saveRate(rate.planId, value ?? 0)}
                      disabled={!canManage}
                    />
                    {canManage && (
                      <ActionButton
                        title="Remover valor"
                        color={theme.error}
                        onClick={() => removeRate(rate.id)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    )}
                  </RateRow>
                ))}

                {canManage && (
                  <RateRow>
                    <Select
                      label="Adicionar plano"
                      value={newRatePlanId}
                      onChange={(event) => setNewRatePlanId(event.target.value)}
                      options={[
                        { value: '', label: 'Escolha um plano' },
                        ...payablePlans
                          .filter((plan) => !editing.rates.some((rate) => rate.planId === plan.id))
                          .map((plan) => ({
                            value: plan.id,
                            label: `${plan.name} · ${formatCurrency(plan.price)}`,
                          })),
                      ]}
                    />
                    <CurrencyInput
                      label="Comissão"
                      allowEmpty
                      value={newRateAmount}
                      onChange={(value) => setNewRateAmount(value ?? 0)}
                    />
                    <Button
                      variant="primary"
                      onClick={() => saveRate(newRatePlanId, newRateAmount)}
                      disabled={!newRatePlanId || newRateAmount <= 0}
                    >
                      <Plus size={16} />
                    </Button>
                  </RateRow>
                )}
              </Section>
            )}
          </>
        )}
      </Drawer>

      {/* --- Fechar competência ------------------------------------------ */}
      <Modal
        isOpen={Boolean(closing)}
        onClose={() => setClosing(null)}
        title={`Fechar competência · ${closing?.customerName ?? ''}`}
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setClosing(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={closeCompetenceFor} isLoading={submitting}>
              Fechar
            </Button>
          </>
        }
      >
        <TextInput
          label="Competência"
          placeholder="AAAA-MM"
          value={closeCompetence}
          onChange={(event) => setCloseCompetence(event.target.value)}
        />
        <Hint>
          Junta as comissões em aberto desta competência num lote e congela o total. Comissão
          cancelada depois não muda o total já fechado.
        </Hint>
      </Modal>

      {/* --- Registrar pagamento ------------------------------------------ */}
      <Modal
        isOpen={Boolean(paying)}
        onClose={() => setPaying(null)}
        title="Registrar pagamento"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPaying(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={registerPayment} isLoading={submitting}>
              Registrar
            </Button>
          </>
        }
      >
        <Hint>
          O pagamento sai fora da plataforma ({paying?.influencerName}:{' '}
          {formatCurrency(paying?.totalAmount ?? 0)}). Aqui se registra que saiu.
        </Hint>
        <TextInput
          label="Comprovante / identificador"
          value={paymentReference}
          onChange={(event) => setPaymentReference(event.target.value)}
        />
      </Modal>

      {/* --- Cancelar comissão -------------------------------------------- */}
      <Modal
        isOpen={Boolean(canceling)}
        onClose={() => setCanceling(null)}
        title="Cancelar comissão"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCanceling(null)} disabled={submitting}>
              Voltar
            </Button>
            <Button
              variant="danger"
              onClick={cancelCommission}
              isLoading={submitting}
              disabled={!cancelReason.trim()}
            >
              Cancelar comissão
            </Button>
          </>
        }
      >
        <TextInput
          label="Motivo"
          placeholder="Estorno da cobrança, cadastro fraudulento…"
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
        />
        <Hint>
          O motivo é obrigatório: comissão que some sem explicação é a que gera a ligação do
          influenciador.
        </Hint>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={endContract}
        title="Encerrar contrato"
        message={`O contrato de ${removing?.customerName ?? ''} deixa de gerar comissão nova. O que já foi apurado continua devido e os fechamentos abertos seguem pagáveis.`}
        confirmLabel="Encerrar"
        danger
      />
    </>
  );
};

export default InfluencersPage;
